import React, { useState } from "react";
import { Box, Typography, Button, Menu as DropdownMenu, MenuItem, IconButton, useTheme, useMediaQuery, Drawer, List, ListItem, ListItemText, Collapse } from "@mui/material";
import { Link } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

interface MenuProps {
  isSticky: boolean;
}

const Menu: React.FC<MenuProps> = ({ isSticky }) => {
  const [activeItem, setActiveItem] = useState("Home");
  const [anchorElServices, setAnchorElServices] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const menuItems = ["Home", "About Us", "Our Services", "Become Associate", "Testimonials"];

  const handleOpenServices = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElServices(event.currentTarget);
  };

  const handleCloseServices = () => {
    setAnchorElServices(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleServicesToggle = () => {
    setServicesOpen(!servicesOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
  };

  const getLink = (item: string) => {
    switch (item) {
      case "Home":
        return "/";
      case "About Us":
        return "/about-us";
      case "Become Associate":
        return "/become-associate"; // Add your Become Associate link here
      case "Testimonials":
        return "/testimonials";
      default:
        return "#"; // Fallback link
    }
  };

  const services = [
    { name: "IEPF Claim", link: "/iepf_claim" },
    { name: "ROC Services", link: "/roc_services" },
    { name: "Duplicate Issue of Shares", link: "/duplicate_shares" },
    { name: "Transmission of Shares", link: "/transmission_shares" },
    { name: "Unclaimed Mutual Fund", link: "/unclaimed_mutual_fund" },
    { name: "Unclaimed Provident Fund", link: "/unclaimed_provident_fund" },
  ];

  return (
    <Box
      sx={{
        position: isSticky ? "fixed" : "absolute",
        top: isSticky ? 0 : "unset",
        left: 0,
        width: "100%",
        backgroundColor: isSticky ? "rgb(0, 0, 0)" : "transparent",
        zIndex: 10,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: {
          xs: "10px 16px", // Mobile padding
          md: "10px 20px", // Desktop padding
        },
        color: "#fff",
        transition: "all 0.3s ease-in-out",
      }}
    >
      {/* Logo */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          color: "#fff",
          fontSize: {
            xs: "20px", // Smaller on mobile
            md: "24px", // Normal on desktop
          },
          letterSpacing: "1px",
        }}
      >
        GetMyShares
      </Typography>
      
      {/* Desktop Menu Items */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            paddingTop: "20px",
            paddingRight: "100px",
          }}
        >
        {/* Static Links */}
        {menuItems.map((item) =>
          item === "Our Services" ? (
            <React.Fragment key={item}>
              <Button
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "16px",
                  fontWeight: 400,
                  textTransform: "none",
                  color: "#fff",
                  "&:hover": {
                    color: "#8BC34A",
                  },
                  transition: "color 0.3s ease-in-out",
                }}
                onClick={handleOpenServices}
              >
                {item}
              </Button>
              <DropdownMenu
                anchorEl={anchorElServices}
                open={Boolean(anchorElServices)}
                onClose={handleCloseServices}
              >
                {services.map((service) => (
                  <MenuItem
                    key={service.name}
                    component={Link}
                    to={service.link}
                    onClick={handleCloseServices}
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: "14px",
                      minWidth: "130px",
                    }}
                  >
                    {service.name}
                  </MenuItem>
                ))}
              </DropdownMenu>
            </React.Fragment>
          ) : (
            <Button
              key={item}
              component={Link}
              to={getLink(item)}
              onClick={() => setActiveItem(item)}
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: "16px",
                fontWeight: 400,
                textTransform: "none",
                color: activeItem === item ? "#8BC34A" : "#fff",
                borderBottom: activeItem === item ? "2px solid #8BC34A" : "none",
                "&:hover": {
                  color: "#8BC34A",
                },
                transition: "color 0.3s ease-in-out, border-bottom 0.3s ease-in-out",
              }}
            >
              {item}
            </Button>
          )
        )}
        </Box>
      )}

      {/* Mobile Menu Button */}
      {isMobile && (
        <IconButton
          color="inherit"
          onClick={handleMobileMenuToggle}
          sx={{ color: "#fff" }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#000',
            color: '#fff',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
            GetMyShares
          </Typography>
          <IconButton onClick={handleMobileMenuClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <List>
          {menuItems.map((item) =>
            item === "Our Services" ? (
              <React.Fragment key={item}>
                <ListItem onClick={handleServicesToggle} sx={{ cursor: 'pointer' }}>
                  <ListItemText 
                    primary={item} 
                    sx={{ 
                      '& .MuiTypography-root': { 
                        fontFamily: "'Poppins', sans-serif",
                        color: '#fff'
                      } 
                    }} 
                  />
                  {servicesOpen ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
                </ListItem>
                <Collapse in={servicesOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {services.map((service) => (
                      <ListItem 
                        key={service.name}
                        component={Link} 
                        to={service.link}
                        onClick={handleMobileMenuClose}
                        sx={{ pl: 4, cursor: 'pointer' }}
                      >
                        <ListItemText 
                          primary={service.name}
                          sx={{ 
                            '& .MuiTypography-root': { 
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '14px',
                              color: '#8BC34A'
                            } 
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ) : (
              <ListItem 
                key={item}
                component={Link} 
                to={getLink(item)}
                onClick={() => {
                  setActiveItem(item);
                  handleMobileMenuClose();
                }}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText 
                  primary={item}
                  sx={{ 
                    '& .MuiTypography-root': { 
                      fontFamily: "'Poppins', sans-serif",
                      color: activeItem === item ? '#8BC34A' : '#fff'
                    } 
                  }} 
                />
              </ListItem>
            )
          )}
        </List>
      </Drawer>
    </Box>
  );
};

export default Menu;